import { useCallback, useEffect, useMemo, useState } from "react";
import TimeStripDaily from "../../../components/Profile/AppointmentCalendar/TimeStripDaily";
import TimeStripWeekly from "../../../components/Profile/AppointmentCalendar/TimeStripWeekly";
import {
  Appointment,
  AppointmentStatus,
} from "../../../interfaces/services/Appointment";
import { Button, Loader, NativeSelect, Skeleton, Tabs } from "@mantine/core";
import TimeStripMonthly from "../../../components/Profile/AppointmentCalendar/TimeStripMonthly";
import { atom, useAtom, useAtomValue } from "jotai";
import { meAtom } from "../../../store/auth";
import appointmentService, {
  AppointmentFilters,
} from "../../../services/api/appointmentService";
import { toFixedISOString } from "../../../utils/dateUtils";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/generateErrorMessage";
import { hasGroup } from "../../../utils/hasGroup";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { Employee, WorkHour } from "../../../interfaces/services/Employee";
import employeeService from "../../../services/api/employeeService";
import { DatePaginationBar } from "../../../components/Profile/AppointmentCalendar/DatePaginationBar";
import { useWindowWidth } from "../../../hooks/windowsWidth";

export const calendarStartDateAtom = atom<Date>(new Date());
export const calendarRangeAtom = atom<"daily" | "weekly" | "monthly">("daily");

export const getStatusColor = (status: AppointmentStatus) => {
  switch (status) {
    case "Approved":
      return "#3366FF";
    case "Pending":
      return "#F59E0B";
    case "Completed":
      return "#2ECC71";
    case "Canceled":
      return "#FF4D4F";
    case "NoShow":
      return "#9E9E9E";
    default:
      return "#3366FF";
  }
};

const EN_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

type DayName = (typeof EN_DAYS)[number];

const defaultWeeklyHours: WorkHour[] = [
  { day: "Monday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Tuesday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Wednesday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Thursday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Friday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Saturday", startTime: "10:00:00", endTime: "22:00:00" },
  { day: "Sunday", startTime: "10:00:00", endTime: "22:00:00" },
];

const toHHmm = (t?: string) => (t ?? "").split(":").slice(0, 2).join(":");

// Çalışandan gelen workHours dizisini 7 güne tamamla (eksik gün varsa default’la doldur)
function normalizeWorkHours(wh?: WorkHour[]): WorkHour[] {
  const base = wh?.length ? wh : [];
  const map = new Map<DayName, WorkHour>();
  for (const item of base) {
    if (EN_DAYS.includes(item.day as DayName)) {
      map.set(item.day as DayName, item);
    }
  }
  for (const d of EN_DAYS) {
    if (!map.has(d)) {
      const def = defaultWeeklyHours.find((x) => x.day === d)!;
      map.set(d, def);
    }
  }
  return EN_DAYS.map((d) => map.get(d)!);
}

// Günlük şerit için, startDate’in gün adına göre "HH:mm" döndür
function getDailyHHmmFromWorkHours(wh: WorkHour[], dayDate: Date) {
  const dayName = EN_DAYS[dayDate.getDay()];
  const found =
    wh.find((w) => w.day === dayName) ??
    defaultWeeklyHours.find((w) => w.day === dayName)!;
  return {
    start: toHHmm(found.startTime) || "09:00",
    end: toHHmm(found.endTime) || "21:00",
  };
}

// ---- Seans etiketi oluşturucu ---------------------------------------------
function buildSessionSuffix(a: Appointment): string {
  const ss = a.serviceSnapshot;
  if (!ss || ss.type !== "Session") return "";
  const cur =
    typeof a.sessionNumber === "number" && a.sessionNumber >= 1
      ? a.sessionNumber
      : "?";
  const total =
    typeof ss.sessionCount === "number" && ss.sessionCount >= 1
      ? ss.sessionCount
      : "?";
  const interval =
    typeof ss.sessionIntervalDays === "number" && ss.sessionIntervalDays >= 1
      ? ` • ${ss.sessionIntervalDays}g`
      : "";
  return ` (${cur}/${total} seans${interval})`;
}

const AppointmentsCalendarPage = () => {
  const navigation = useNavigate();
  const windowWidth = useWindowWidth();

  const me = useAtomValue(meAtom);
  const groupPermissions = {
    Receptionist: hasGroup(me, "Receptionist"),
    Administrator: hasGroup(me, "Administrator"),
    createAppointmentCustomerFree: hasGroup(
      me,
      "create-appointment-customer-free",
    ),
    createAppointmentCustomerFreeAll: hasGroup(
      me,
      "create-appointment-customer-free-all",
    ),
    updateAppointmentStatus: hasGroup(me, "update-appointment-status"),
    updateAppointmentStatusAll: hasGroup(me, "update-appointment-status-all"),
  };

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [range, setRange] = useAtom<"daily" | "weekly" | "monthly">(
    calendarRangeAtom,
  );
  const [startDate, setStartDate] = useAtom<Date>(calendarStartDateAtom);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);

  const buildFilters = useCallback((): AppointmentFilters => {
    let start: Date;
    let end: Date;

    switch (range) {
      case "daily": {
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(startDate);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case "weekly": {
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(startDate);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case "monthly": {
        start = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          1,
          0,
          0,
          0,
          0,
        );
        const lastDay = new Date(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          0,
        );
        end = new Date(
          lastDay.getFullYear(),
          lastDay.getMonth(),
          lastDay.getDate(),
          23,
          59,
          59,
          999,
        );
        break;
      }
    }

    const base: AppointmentFilters = {
      startDate: toFixedISOString(start),
      endDate: toFixedISOString(end),
    };

    return selectedEmployee?.code
      ? ({ ...base, employeeCode: selectedEmployee.code } as any)
      : base;
  }, [range, startDate, selectedEmployee?.code]);

  const fetchAppointments = useCallback(async () => {
    if (!me?.salonCode) return;
    setLoading(true);
    try {
      const filters = buildFilters();
      const appts = await appointmentService.getFilteredAppointments(
        me.salonCode,
        filters,
      );
      setAppointments(appts);
    } catch (err: any) {
      if (err.status !== 404)
        toast.error(getErrorMessage(err.status), {
          toastId: "get-appointments",
        });
    } finally {
      setLoading(false);
    }
  }, [me?.salonCode, buildFilters, setAppointments, setLoading]);

  const fetchEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const list = await employeeService.getEmployeeWithSalonCode(
        me?.salonCode!,
      );
      setEmployees(list || []);

      const mine = list?.find((e) => e.userCode === me?.code);
      if (mine) setSelectedEmployee(mine);
      else setSelectedEmployee(undefined);
    } catch (err) {
    } finally {
      setEmployeesLoading(false);
    }
  }, [me?.salonCode]);

  const weeklyWorkHours = useMemo(
    () => normalizeWorkHours(selectedEmployee?.workHours),
    [selectedEmployee?.workHours],
  );

  const { start: dailyStartHHmm, end: dailyEndHHmm } = useMemo(
    () => getDailyHHmmFromWorkHours(weeklyWorkHours, startDate),
    [weeklyWorkHours, startDate],
  );

  // ---- Randevuları seans etiketi ile zenginleştir (immutable) --------------
  const appointmentsDecorated = useMemo<Appointment[]>(() => {
    if (!appointments?.length) return appointments;
    return appointments.map((a) => {
      const suffix = buildSessionSuffix(a);
      if (!suffix || !a.serviceSnapshot) return a;
      const name = a.serviceSnapshot.name || "Randevu";
      return {
        ...a,
        serviceSnapshot: {
          ...a.serviceSnapshot,
          // Örn: "Cilt Bakımı (3/6 seans • 7g)"
          name: `${name}${suffix}`,
        },
      };
    });
  }, [appointments]);

  useEffect(() => {
    fetchAppointments();
  }, [range, startDate, fetchAppointments, selectedEmployee?.code]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  return (
    <div className="flex flex-col h-full p-3 md:p-0">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <p
            className="hidden md:block md:text-xl font-bold text-text-light dark:text-text-dark"
            id="envanter"
          >
            Profil takvimi
          </p>
          {employeesLoading ? (
            <Skeleton height={26} width={100} />
          ) : (
            <NativeSelect
              size="md"
              data={[
                ...employees.map((e) => ({
                  value: e.code,
                  label:
                    `${e.userSnapshot?.name ?? ""} ${
                      e.userSnapshot?.lastname ?? ""
                    }`.trim() || "Personel",
                })),
              ]}
              value={selectedEmployee?.code}
              onChange={(e) => {
                const val = e.currentTarget.value;
                const found = employees.find((x) => x.code === val);
                setSelectedEmployee(found);
              }}
              aria-label="Çalışan filtresi"
            />
          )}
        </div>
        {(groupPermissions.createAppointmentCustomerFree ||
          groupPermissions.createAppointmentCustomerFreeAll ||
          groupPermissions.Receptionist) && (
          <Button
            aria-label="Yeni Randevu Ekle"
            variant="transparent"
            onClick={() => {
              navigation("/profil/randevular/ekle");
            }}
            id="new-appointment-button"
            leftSection={<FaPlus />}
            size="lg"
          >
            Yeni Randevu
          </Button>
        )}
      </div>
      <div className="flex-1 w-full overflow-auto">
        {loading && (
          <div className="w-full h-full flex items-center justify-center">
            <Loader />
          </div>
        )}
        {!loading && (
          <Tabs
            variant="pills"
            value={range}
            onChange={(val) => setRange(val as "daily" | "weekly" | "monthly")}
          >
            <Tabs.List grow={true}>
              <Tabs.Tab
                value="daily"
                className={
                  range === "daily"
                    ? "font-bold"
                    : "bg-blue-500/5 font-bold hover:bg-blue-500/20"
                }
              >
                Günlük
              </Tabs.Tab>
              <Tabs.Tab
                value="weekly"
                className={
                  range === "weekly"
                    ? "font-bold"
                    : "bg-blue-500/5 font-bold hover:bg-blue-500/20"
                }
              >
                Haftalık
              </Tabs.Tab>
              <Tabs.Tab
                value="monthly"
                className={
                  range === "monthly"
                    ? "font-bold"
                    : "bg-blue-500/5 font-bold hover:bg-blue-500/20"
                }
              >
                Aylık
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="daily">
              <TimeStripDaily
                start={dailyStartHHmm}
                end={dailyEndHHmm}
                dayDate={startDate}
                minuteHeight={1.5}
                appointments={appointmentsDecorated}
                groupPermissions={groupPermissions}
                fetchAppointments={fetchAppointments}
              />
            </Tabs.Panel>
            <Tabs.Panel value="weekly">
              <TimeStripWeekly
                workHours={weeklyWorkHours}
                weekStartDate={startDate}
                minuteHeight={1.5}
                appointments={appointmentsDecorated}
                groupPermissions={groupPermissions}
                fetchAppointments={fetchAppointments}
              />
            </Tabs.Panel>
            <Tabs.Panel value="monthly">
              <TimeStripMonthly
                monthDate={startDate}
                appointments={appointmentsDecorated}
              />
            </Tabs.Panel>
          </Tabs>
        )}
        {!loading && (
          <DatePaginationBar
            value={startDate}
            onChange={setStartDate}
            windowSize={windowWidth > 768 ? 3 : 1}
          />
        )}
      </div>
    </div>
  );
};

export default AppointmentsCalendarPage;
